import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getBrokerConfig, getBrokerFromPath, DEFAULT_BROKER } from '../config/brokerConfig';

// Create context
const BrokerContext = createContext(null);

// Provider component
export function BrokerProvider({ children }) {
  const location = useLocation();
  const [currentBrokerId, setCurrentBrokerId] = useState(DEFAULT_BROKER);

  // Update broker based on URL path
  useEffect(() => {
    const brokerId = getBrokerFromPath(location.pathname);
    setCurrentBrokerId(brokerId);
  }, [location.pathname]);

  // Get current broker config
  const brokerConfig = useMemo(() => {
    return getBrokerConfig(currentBrokerId);
  }, [currentBrokerId]);

  // Helper to get broker-prefixed path
  const getBrokerPath = (path) => {
    // For admin routes, don't add broker prefix
    if (path.startsWith('/admin')) {
      return path;
    }
    // For user routes, add broker prefix
    const basePath = brokerConfig.basePath;
    if (path === '/') {
      return basePath;
    }
    return `${basePath}${path}`;
  };

  // Helper to strip broker prefix from path
  const getRelativePath = (fullPath) => {
    const basePath = brokerConfig.basePath;
    if (fullPath.startsWith(basePath)) {
      const relative = fullPath.slice(basePath.length);
      return relative || '/';
    }
    return fullPath;
  };

  // Apply theme CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const theme = brokerConfig.theme;

    root.style.setProperty('--broker-primary', brokerConfig.primaryColor);
    root.style.setProperty('--broker-secondary', brokerConfig.secondaryColor);
    root.style.setProperty('--broker-accent', brokerConfig.accentColor);
    root.style.setProperty('--broker-gradient', theme.headerGradient);
    root.style.setProperty('--broker-button', theme.buttonColor);
    root.style.setProperty('--broker-button-hover', theme.buttonHoverColor);
    root.style.setProperty('--broker-link', theme.linkColor);
    root.style.setProperty('--broker-badge-bg', theme.badgeColor);
    root.style.setProperty('--broker-badge-text', theme.badgeTextColor);
  }, [brokerConfig]);

  const value = {
    brokerId: currentBrokerId,
    brokerConfig,
    setBrokerId: setCurrentBrokerId,
    getBrokerPath,
    getRelativePath,
    // Shorthand accessors
    brokerName: brokerConfig.name,
    brokerLogo: brokerConfig.logo,
    refPrefix: brokerConfig.refPrefix,
    theme: brokerConfig.theme
  };

  return (
    <BrokerContext.Provider value={value}>
      {children}
    </BrokerContext.Provider>
  );
}

// Custom hook to use broker context
export function useBroker() {
  const context = useContext(BrokerContext);
  if (!context) {
    throw new Error('useBroker must be used within a BrokerProvider');
  }
  return context;
}

export default BrokerContext;
